import { useState, useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import NativeLocation from "@/plugins/native-location";
import NativeCamera from "@/plugins/native-camera";
import NativeNotification from "@/plugins/native-notification";

export type PermissionType = "location" | "camera" | "notifications";

interface UseOnboardingResult {
  needsOnboarding: boolean;
  isChecking: boolean;
  pendingPermissions: PermissionType[];
  completeOnboarding: () => void;
}

const ONBOARDING_COMPLETED_KEY = "onboarding-completed";

export function useOnboarding(): UseOnboardingResult {
  const [isChecking, setIsChecking] = useState(true);
  const [pendingPermissions, setPendingPermissions] = useState<PermissionType[]>([]);

  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (!isNative) {
      setIsChecking(false);
      return;
    }

    // If already completed onboarding, skip
    const completed = localStorage.getItem(ONBOARDING_COMPLETED_KEY);
    if (completed === "true") {
      setIsChecking(false);
      return;
    }

    const checkPermissions = async () => {
      const pending: PermissionType[] = [];

      try {
        // Check location
        const locationStatus = await NativeLocation.getPermissionStatus();
        if (locationStatus.statusName !== "authorizedAlways" &&
            locationStatus.statusName !== "authorizedWhenInUse") {
          pending.push("location");
        }

        // Check camera
        const cameraStatus = await NativeCamera.getPermissionStatus();
        if (cameraStatus.statusName !== "authorized") {
          pending.push("camera");
        }

        // Check notifications
        const notificationStatus = await NativeNotification.getPermissionStatus();
        if (notificationStatus.statusName !== "authorized" &&
            notificationStatus.statusName !== "provisional") {
          pending.push("notifications");
        }
      } catch (error) {
        console.error("Failed to check permissions:", error);
      }

      setPendingPermissions(pending);
      setIsChecking(false);
    };

    checkPermissions();
  }, [isNative]);

  const completeOnboarding = useCallback(() => {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, "true");
    setPendingPermissions([]);
  }, []);

  return {
    needsOnboarding: pendingPermissions.length > 0,
    isChecking,
    pendingPermissions,
    completeOnboarding,
  };
}
